import { getUserContainer } from '../../container/user-module'
import { IUserService, LOGGED_USER_SERVICE_KEY, USER_SERVICE_KEY } from '../../modules/users'
import { Box, Modal, Tooltip } from '@mui/material'
import { AppTable, Field } from '../../components/table'
import { fromModel, User, UserDTO, UserQuery } from '../../modules/users/models/User'
import { useTranslation } from 'react-i18next'
import React, { useEffect, useState } from 'react'
import { BoolQueryParam, Query, QueryParam, QueryParamN } from '../../common/api/Query'
import { Actions, Pager, Search, SearchValue } from '../../components/table/types'
import iconView from '../../assets/table_icons/view.svg'
import iconDelete from '../../assets/table_icons/delete.svg'
import calendarIcon from '../../assets/table_icons/calendar.svg'
import addPatientIcon from '../../assets/users/addPatient.svg'
import searchIcon from '../../assets/users/searchIcon.svg'
import {
  ROUTE_CALENDAR_EXERCISES,
  ROUTE_DASHBOARD,
  ROUTE_PATIENTS,
  ROUTE_STATISTICS,
  ROUTE_USERS_CREATE,
} from '../../routes/routes-constants'
import { navigate } from '@reach/router'
import { UserGender } from '../../modules/users/enums/UserGender'
import editIcon from '../../assets/table_icons/edit.svg'
import addFamiliarIcon from '../../assets/table_icons/add-familiar.svg'
import { LoggedUserService } from '../../modules/users/services/LoggedUserService'
import { CustomModal } from '../../components/modal/CustomModal'
import { useSnackbar } from 'notistack'
import { Pagination } from '../../components/table/Pagination'
import { Searcher } from '../../components/table/Searcher'
import { ModalFamiliar } from './ModalFamiliar'
import { useUserRoles } from '../../modules/users/hooks/useUserRoles'
import { PatientFamilyDTO } from '../../modules/users/models/PatientFamily'
import { PageLayout } from 'pages/layout'

const userContainer = getUserContainer()
const userService = userContainer.get<IUserService>(USER_SERVICE_KEY)
const loggedUserService = userContainer.get<LoggedUserService>(LOGGED_USER_SERVICE_KEY)

export function Table() {
  const { t } = useTranslation()

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [users, setUsers] = useState<User[]>([])
  const [count, setCount] = useState<number>(0)
  const [doctors, setDoctors] = useState<User[]>([])
  const [page, setPage] = useState<number>(0)
  const [pager, setPager] = useState<Pager>()
  const [rowsPerPage, setRowsPerPage] = useState<number>(10)
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false)
  const [currentUser, setCurrentUser] = useState<User>()
  const { enqueueSnackbar } = useSnackbar()
  const [selectedPatient, setSelectedPatient] = useState<User | null>(null)
  const [loggedUserData, setLoggedUserData] = useState<User | null>(null)
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0)
  const [patientIds, setPatientIds] = useState<string[]>([])

  // ↓ openMenuLeft y innerWidth ELIMINADOS — los gestiona PageLayout

  const loggedUser = loggedUserService.get()
  const {
    isFamiliar: isLoggedUserFamiliar,
    canCreatePatient,
    canEditPatientData,
    canManagePatientCircle,
  } = useUserRoles(loggedUser)

  const [searcher, setSearcher] = useState<SearchValue<UserQuery>[]>([
    {
      name: 'firstName',
      label: t('search') + '...',
    },
  ])

  useEffect(() => {
    const loggedUser = loggedUserService.get()
    if (loggedUser?.id) {
      userService.getByID(loggedUser.id).subscribe((userData) => {
        if (userData) setLoggedUserData(userData)
      })
    }
  }, [])

  useEffect(() => {
    if (!loggedUserData || !isLoggedUserFamiliar) return
    const familiarId = loggedUserService.get()?.id
    if (!familiarId) return
    userService.getPatientsByFamiliar(familiarId).subscribe((families: PatientFamilyDTO[]) => {
      setPatientIds(families.map((f) => f.patientID))
    })
  }, [loggedUserData, isLoggedUserFamiliar])

  useEffect(() => {
    if (!loggedUserData) return
    const loggedUser = loggedUserService.get()
    setIsLoading(true)

    let queryParams: (QueryParam<UserQuery> | QueryParamN<UserQuery>)[] = [
      { name: 'isPatient', value: new BoolQueryParam(true) },
      ...searcherQuery(searcher),
    ]

    if (isLoggedUserFamiliar) {
      if (patientIds.length > 0) {
        queryParams.push({ name: 'ids', value: patientIds })
      } else {
        setUsers([])
        setCount(0)
        setIsLoading(false)
        return
      }
    } else {
      queryParams.push({ name: 'assignedID', value: loggedUser?.id || '' })
    }

    userService
      .getFilteredList(
        new Query({
          query: queryParams,
          pager: { offset: page * rowsPerPage, limit: rowsPerPage },
        })
      )
      .subscribe((res) => {
        setUsers(res.items)
        setCount(res.count)
        setIsLoading(false)
      })
  }, [
    loggedUserData,
    searcher,
    page,
    rowsPerPage,
    isLoggedUserFamiliar,
    patientIds,
    refreshTrigger,
  ])

  useEffect(() => {
    if (!users.length) return
    const ids = users.map((u) => u.assignedID).filter(Boolean)
    if (!ids.length) return
    userService
      .getFilteredList(
        new Query({ pager: { offset: 0, limit: ids.length }, query: [{ name: 'ids', value: ids }] })
      )
      .subscribe((res) => setDoctors(res.items))
  }, [users])

  useEffect(() => {
    setPager({ page, count, handleChangePage, rowsPerPage, handleChangeRowsPerPage })
  }, [page, count, rowsPerPage])

  const handleChangePage = (_: unknown, value: number) => setPage(value)
  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number.isNaN(e.target.value) ? 10 : Number.parseInt(e.target.value))
    setPage(0)
  }

  const handleCloseDeleteModal = () => setOpenDeleteModal(false)
  const handleDeleteUser = () => {
    if (!currentUser) return
    const userUnassigned = new User(currentUser)
    userUnassigned.assignedID = ''
    userService.update(fromModel(userUnassigned)).subscribe(() => {
      setOpenDeleteModal(false)
      enqueueSnackbar(t('userUnassigned'), { variant: 'success' })
      setRefreshTrigger((prev) => prev + 1)
    })
  }

  const fields: Field<User>[] = [
    {
      name: 'firstName',
      label: t('fullName'),
      renderFunc: (f, i) => i.firstName + ' ' + i.lastName,
    },
    { name: 'historyRecordNumber', label: t('historyRecordNumber') },
    { name: 'diagnosisSummary', label: t('diagnosisSummary') },
    {
      name: 'assignedID',
      label: t('assignedID'),
      renderFunc: (f, i) => {
        if (!i.assignedID) return ''
        const d = doctors.find((d) => d.id === i.assignedID)
        if (!d?.lastName) return ''
        return t(d.gender === UserGender.Female ? 'femaleDoctor' : 'maleDoctor') + ' ' + d.lastName
      },
    },
  ]

  const search: Search<UserQuery> = {
    searchValues: searcher,
    handleSearch: (svs) => {
      setSearcher(svs)
      setPage(0)
    },
  }

  const actions: Actions<User> = {
    actionsColumn: t('actions'),
    items: [
      {
        handler: (u) => navigate(`${ROUTE_CALENDAR_EXERCISES}/${u.id}`),
        icon: calendarIcon,
        label: 'calendar',
        hidden: () => false,
      },
      {
        handler: (u) => navigate(`${ROUTE_DASHBOARD}/${u.id}`),
        icon: iconView,
        label: 'see',
        hidden: () => false,
      },
      {
        handler: (u) => navigate(`${ROUTE_USERS_CREATE}/${u.id}`),
        icon: editIcon,
        label: 'edit',
        hidden: () => !canEditPatientData || isLoggedUserFamiliar,
      },
      {
        handler: (u) => setSelectedPatient(u),
        icon: addFamiliarIcon,
        label: 'familiar',
        hidden: () => !canManagePatientCircle || isLoggedUserFamiliar,
      },
      {
        handler: (u) => {
          setCurrentUser(u)
          setOpenDeleteModal(true)
        },
        icon: iconDelete,
        label: 'delete',
        hidden: () => isLoggedUserFamiliar,
      },
    ],
  }

  // FAB: se queda aquí porque solo existe en esta página
  const fab =
    canCreatePatient && !isLoggedUserFamiliar ? (
      <Tooltip title={t('addPatient')}>
        <img
          src={addPatientIcon}
          alt="add patient"
          width={90}
          onClick={() => navigate(ROUTE_USERS_CREATE)}
        />
      </Tooltip>
    ) : undefined

  return (
    <PageLayout>
      <Modal open={openDeleteModal} onClose={handleCloseDeleteModal}>
        <CustomModal
          title={t('unassignPatient')}
          warningText={t('irreversibleUnassignPatientAction')}
          handleClose={handleCloseDeleteModal}
          handleSave={handleDeleteUser}
        />
      </Modal>

      <AppTable
        rowKeyField="id"
        items={users}
        fields={fields}
        actions={actions}
        isLoading={isLoading}
      />

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        px={1}
        sx={{ flexShrink: 0, flexWrap: 'wrap', gap: 1 }}
      >
        <Box>{pager && <Pagination {...pager} />}</Box>
        <Box display="flex" alignItems="center" mr={10}>
          <img style={{ marginRight: 2, marginBottom: 2 }} src={searchIcon} alt="search patient" />
          <Searcher search={search} />
        </Box>
      </Box>

      {fab && (
        <Box style={{ position: 'absolute', right: 20, bottom: 105, cursor: 'pointer' }}>{fab}</Box>
      )}

      {selectedPatient && (
        <ModalFamiliar
          open={Boolean(selectedPatient)}
          onClose={() => setSelectedPatient(null)}
          patientID={selectedPatient.id}
        />
      )}
    </PageLayout>
  )
}

const searcherQuery = (
  svs: SearchValue<UserQuery>[]
): QueryParam<UserQuery>[] | QueryParamN<UserQuery>[] =>
  svs.filter((sv) => sv.value).map((sv) => ({ name: sv.name, value: sv.value as string }))
